import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Autocomplete, Grid, Box, Alert, CircularProgress, Typography
} from '@mui/material';
import { itemDraft, itemPayload, validateItemDraft } from '../lib/invoiceItemEditor.mjs';

export default function InvoiceItemFormModal({ open, onClose, onSave, supabase, authState, item = null, product = null }) {
  const [formData, setFormData] = useState(() => itemDraft());
  const [errors, setErrors] = useState({});
  const [saveError, setSaveError] = useState('');
  const [lineSaved, setLineSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [productOptions, setProductOptions] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [searchError, setSearchError] = useState('');
  const [isSearchingProducts, setIsSearchingProducts] = useState(false);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const baseline = useRef(itemDraft());
  const busy = useRef(false);
  const formRef = useRef(null);
  const editing = item?.id != null;
  const prorated = item?.original_unit_price != null;
  const userId = authState?.user?.id;

  useEffect(() => {
    if (!open) return;
    const initial = itemDraft(item);
    baseline.current = initial;
    setFormData(initial); setErrors({}); setSaveError(''); setLineSaved(false); setSearchError(''); setDiscardOpen(false);
    setProductSearch(initial.product_id ? `${product?.item_code || ''} ${product?.description || 'Producto vinculado'}`.trim() : ''); setProductOptions([]); setAutocompleteOpen(false);
  }, [open, item]);

  useEffect(() => {
    if (!open || !autocompleteOpen || !userId || !supabase) { setIsSearchingProducts(false); return; }
    let active = true;
    setIsSearchingProducts(true); setSearchError('');
    const timeoutId = setTimeout(async () => {
      try {
        let query = supabase.from('invoice_products').select('*').eq('tenant_id',userId).limit(50);
        const term = productSearch.trim().replace(/[(),]/g,' ');
        if (term) query = query.or(`item_code.ilike.%${term}%,description.ilike.%${term}%`);
        const {data,error} = await query;
        if (error) throw error;
        if (active) setProductOptions(data || []);
      } catch {
        if (active) setSearchError('No se pudieron cargar los productos. Intenta buscar de nuevo.');
      } finally {
        if (active) setIsSearchingProducts(false);
      }
    },300);
    return () => { active = false; clearTimeout(timeoutId); };
  }, [open, autocompleteOpen, productSearch, userId, supabase]);

  const requestClose = () => {
    if (busy.current) return;
    if (lineSaved || Object.keys(baseline.current).some(key => String(formData[key] ?? '') !== String(baseline.current[key] ?? ''))) setDiscardOpen(true);
    else onClose();
  };
  const handleChange = event => {
    const {name,value} = event.target;
    setFormData(prev => ({...prev,[name]:value}));
    setErrors(prev => ({...prev,[name]:undefined}));
  };
  const handleSave = async event => {
    event.preventDefault();
    if (busy.current) return;
    const nextErrors = validateItemDraft(formData);
    setErrors(nextErrors); setSaveError('');
    if (Object.keys(nextErrors).length) {
      formRef.current?.querySelector(`[name="${Object.keys(nextErrors)[0]}"]`)?.focus();
      return;
    }
    busy.current = true; setSaving(true);
    try { await onSave(itemPayload(formData,item)); }
    catch (error) {
      console.error('No se pudo guardar la línea de factura:',error);
      setLineSaved(error?.lineSaved === true);
      const message = error?.message || '';
      setSaveError(/^(Tu sesión|La línea|La edición de líneas|Restaura|Revisa)/.test(message) ? message : error?.code === '42501'
        ? 'No tienes permiso para modificar esta línea. Tus cambios siguen aquí; revisa tu sesión.'
        : 'No se pudo guardar la línea. Tus cambios siguen aquí; comprueba la conexión y vuelve a intentarlo.');
    }
    finally { busy.current = false; setSaving(false); }
  };
  const selectedProduct = productOptions.find(option => String(option.id) === String(formData.product_id)) ||
    (formData.product_id ? product?.id && String(product.id) === String(formData.product_id) ? product : {id:formData.product_id,description:'Producto vinculado',item_code:''} : null);
  const field = (name,label,extra={}) => <TextField name={name} label={label} value={formData[name]} onChange={handleChange}
    fullWidth size="small" disabled={saving} error={!!errors[name]} helperText={errors[name]} {...extra} />;

  return <>
    <Dialog open={open} onClose={requestClose} maxWidth="sm" fullWidth aria-labelledby="invoice-item-title">
      <Box component="form" noValidate ref={formRef} onSubmit={handleSave}
        onKeyDown={event => { if (event.key === 'Enter' && event.nativeEvent.isComposing) event.preventDefault(); }}>
        <DialogTitle id="invoice-item-title">{editing ? 'Editar línea' : 'Añadir línea'}</DialogTitle>
        <DialogContent dividers sx={{maxHeight:'65dvh',overflowY:'auto'}}>
          {saveError && <Alert severity="error" sx={{mb:2}}>{saveError}</Alert>}
          {prorated && <Alert severity="info" sx={{mb:2}}>Esta línea tiene flete prorrateado. Para cambiar cantidad, precio o descuento, usa «Restaurar precios originales» en la factura y vuelve a editarla.</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete open={autocompleteOpen} onOpen={() => setAutocompleteOpen(true)} onClose={() => setAutocompleteOpen(false)}
                options={productOptions} loading={isSearchingProducts} disabled={saving} inputValue={productSearch}
                noOptionsText="No se encontraron productos" loadingText="Buscando productos…" clearText="Limpiar" closeText="Cerrar" openText="Abrir productos"
                onInputChange={(event,value,reason) => { if (reason !== 'reset') setProductSearch(value); }}
                getOptionLabel={option => typeof option === 'string' ? option : `${option.item_code || ''} ${option.description || ''}`.trim()}
                isOptionEqualToValue={(option,value) => String(option.id) === String(value.id)} value={selectedProduct}
                onChange={(event,value) => {
                  setProductSearch(value ? `${value.item_code || ''} ${value.description || ''}`.trim() : '');
                  setFormData(prev => ({...prev,product_id:value?.id ?? null,
                    description:value?.description || prev.description,
                    unit_price:!prorated && value?.price != null ? value.price : prev.unit_price}));
                }} renderInput={params => <TextField {...params} label="Buscar producto/servicio" size="small" fullWidth
                  error={!!searchError} helperText={searchError || (editing && selectedProduct ? `Producto actual: ${selectedProduct.item_code || ''} ${selectedProduct.description || ''}` : '')}
                  InputProps={{...params.InputProps,endAdornment:<>{isSearchingProducts && <CircularProgress color="inherit" size={18} />}{params.InputProps.endAdornment}</>}} />} />
            </Grid>
            <Grid item xs={12}>{field('provider_item_code','Código del ítem (proveedor)')}</Grid>
            <Grid item xs={12}>{field('description','Descripción',{required:true,autoFocus:true})}</Grid>
            <Grid item xs={12} sm={4}>{field('quantity','Cantidad',{type:'number',required:true,disabled:saving || prorated,inputProps:{min:0,step:'any'}})}</Grid>
            <Grid item xs={12} sm={8}>{field('unit_price','Precio unitario antes de descuento',{type:'number',required:true,disabled:saving || prorated,inputProps:{min:0,step:'any'},helperText:errors.unit_price || 'Sin IVA ni INC. El descuento se aplica una sola vez.'})}</Grid>
            {[['tax','IVA'],['impuesto_consumo','INC'],['discount','Descuento de la línea']].map(([name,label]) =>
              <Grid key={name} item xs={12} sm={4}>{field(name,label,{type:'number',disabled:saving || name==='discount' && prorated,inputProps:{min:0,step:'any'}})}</Grid>)}
          </Grid>
          <Typography variant="caption" component="p" color="text.secondary" sx={{mt:2}}>IVA, INC y descuento son importes totales de esta línea.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={requestClose} color="inherit" disabled={saving}>Cancelar</Button>
          <Button type="submit" color="primary" variant="contained" disabled={saving} sx={{minWidth:editing?155:100}}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>{saving ? 'Guardando…' : editing ? 'Guardar cambios' : 'Guardar'}</Button>
        </DialogActions>
      </Box>
    </Dialog>
    <Dialog open={discardOpen} onClose={() => setDiscardOpen(false)} aria-labelledby="discard-item-title">
      <DialogTitle id="discard-item-title">{lineSaved ? '¿Cerrar con los totales pendientes?' : '¿Descartar los cambios de la línea?'}</DialogTitle>
      <DialogContent>{lineSaved ? 'La línea ya quedó guardada. Vuelve a guardar para actualizar los totales antes de aprobar la factura.' : 'Los cambios sin guardar se perderán.'}</DialogContent>
      <DialogActions>
        <Button autoFocus onClick={() => setDiscardOpen(false)}>Seguir editando</Button>
        <Button color="error" onClick={() => {setDiscardOpen(false);onClose();}}>{lineSaved ? 'Cerrar editor' : 'Descartar cambios'}</Button>
      </DialogActions>
    </Dialog>
  </>;
}
